/**
 * /app/$tenantSlug/attendance/approvals — Regularizations queue
 * (manager / HR). Pending corrections → approve / reject. Scope is
 * server-enforced (manager = reports, HR = entity). Reject requires a note.
 */

import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import { TimeAndLeaveShell } from "@/components/shared/time-and-leave-shell";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useRegularizations,
  useApproveRegularization,
  useRejectRegularization,
} from "@/hooks/use-attendance";
import type { Regularization } from "@/types/attendance";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/attendance/approvals",
)({
  component: ApprovalsRoute,
});

function ApprovalsRoute() {
  const queue = useRegularizations("pending");
  const approve = useApproveRegularization();
  const reject = useRejectRegularization();
  const [rejecting, setRejecting] = useState<Regularization | null>(null);
  const [note, setNote] = useState("");

  async function doApprove(r: Regularization) {
    try {
      await approve.mutateAsync({ id: r.id });
      toast.success("Approved");
    } catch {
      toast.error("Failed to approve");
    }
  }

  async function doReject() {
    if (!rejecting) return;
    if (!note.trim()) {
      toast.error("A reason is required to reject");
      return;
    }
    try {
      await reject.mutateAsync({ id: rejecting.id, body: { decision_notes: note.trim() } });
      toast.success("Rejected");
      setRejecting(null);
      setNote("");
    } catch {
      toast.error("Failed to reject");
    }
  }

  return (
    <TimeAndLeaveShell
      title="Attendance regularizations"
      description="Corrections awaiting your sign-off."
    >
      <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Pending</CardTitle>
        </CardHeader>
        <CardContent>
          {queue.isLoading ? (
            <p className="text-muted-foreground text-sm">Loading…</p>
          ) : queue.data && queue.data.regularizations.length > 0 ? (
            <ul className="divide-y">
              {queue.data.regularizations.map((r) => (
                <li key={r.id} className="space-y-1 py-3">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">
                      #{r.employee_id} — {r.work_date}
                    </span>
                    <Badge variant="outline">{r.reason_type}</Badge>
                  </div>
                  <p className="text-muted-foreground text-sm">{r.reason_note}</p>
                  <div className="flex gap-2 pt-1">
                    <Button size="sm" onClick={() => doApprove(r)} disabled={approve.isPending}>
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setRejecting(r);
                        setNote("");
                      }}
                    >
                      Reject
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-muted-foreground text-sm">Nothing pending.</p>
          )}
        </CardContent>
      </Card>

      <Dialog open={rejecting !== null} onOpenChange={(o) => !o && setRejecting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject regularization</DialogTitle>
          </DialogHeader>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Reason for rejection (required)"
            maxLength={2000}
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejecting(null)}>
              Cancel
            </Button>
            <Button onClick={doReject} disabled={reject.isPending}>
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </TimeAndLeaveShell>
  );
}
